import {
  Avatar,
  Card,
  CardBody,
  CardHeader,
  DataField,
  DataValue,
  InfoNote,
  Page,
  PageBody,
  PageHeader,
  SwitchField,
  Tag,
} from '@ghp/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute } from '@tanstack/react-router'
import {
  Cloud,
  Info,
  Lock,
  MapPin,
  PenTool,
  SlidersHorizontal,
  TriangleAlert,
  User,
  Wifi,
} from 'lucide-react'
import { useState, useSyncExternalStore, type ReactNode } from 'react'

import { useDocumentTitle } from '../../../app/document-title'
import { initialsOf, nameOf, shellRoute } from '../../../app/routes/root'
import {
  DEVICE_PREFERENCE_DEFAULTS,
  readDevicePreferences,
  saveDevicePreference,
  type ResolvedDevicePreferences,
} from '../../../offline/preferences'
import { PREFERENCES_QUERY_KEY } from '../../../offline/query-keys'
import s from './settings.module.css'

/**
 * « Réglages » — le compte ouvert sur cette tablette, et les deux réglages que l'app sait
 * réellement honorer.
 *
 * ⚠️ **Trois interrupteurs du prototype validé ne sont pas repris**, et chacun pour la même
 * raison : la capacité qu'il commanderait n'existe pas. « Stylet pour la signature » (la zone de
 * signature ne distingue pas le stylet du doigt), « Rappels de RDV sur l'appareil » (l'app
 * n'émet aucune notification locale), et « Mode hors ligne », dont le prototype dit lui-même
 * « Garder les tournées disponibles sans réseau » : c'est D22, la propriété que le cache et le
 * précache tiennent en permanence, pas une option — et l'éteindre voudrait dire purger, ce que
 * D22 interdit avant tout le reste. Trois manques à porter au produit, pas trois oublis.
 *
 * ⚠️ **Pas de bouton « Enregistrer »** (prototype `:298`) : chaque réglage est écrit au moment où
 * il est commuté. Un bouton qui n'enregistrerait rien laisserait l'agent croire que partir sans
 * l'appuyer annule son geste.
 *
 * ⚠️ Et **la carte s'appelle « Contrôle terrain », pas « Contrôle terrain & rappels »** : la
 * moitié « rappels » du titre du prototype n'a aucune ligne, et un titre qui annonce ce qui n'y
 * est pas se lit comme une fonction cassée.
 */
export const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  component: Settings,
})

function Settings() {
  const { auth, shared } = settingsRoute.useRouteContext()
  const session = useSyncExternalStore(auth.subscribe, auth.getSnapshot, auth.getSnapshot)
  const queryClient = useQueryClient()
  const [writeFailed, setWriteFailed] = useState(false)

  useDocumentTitle('Réglages')

  /**
   * Les réglages, lus depuis Dexie comme tout le reste (D21).
   *
   * Le repli sur les défauts pendant la première lecture n'est pas un placebo : ce sont les
   * valeurs sous lesquelles l'app tourne tant que rien n'a été réglé, donc ce qu'un interrupteur
   * doit montrer. Peindre « éteint » le temps d'une lecture ferait clignoter les deux rangées.
   */
  const preferences = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => readDevicePreferences(shared),
  })
  const settings = preferences.data ?? DEVICE_PREFERENCE_DEFAULTS

  async function commute(key: keyof ResolvedDevicePreferences, value: boolean): Promise<void> {
    setWriteFailed(false)

    try {
      await saveDevicePreference(shared, key, value)
    } catch (error) {
      // ⚠️ L'interrupteur **revient à sa place** puisqu'il n'a jamais quitté l'état lu : il est
      // contrôlé, et rien n'a été écrit. C'est l'aveu honnête d'un réglage non enregistré — le
      // laisser bouger dirait à l'agent que le GPS est coupé alors qu'il tournera encore demain.
      console.error('[terrain] Réglage non enregistré.', error)
      setWriteFailed(true)
      return
    }

    await queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY })
  }

  // La coquille ne se rend jamais déconnectée (garde de `shellRoute`) : rien à afficher.
  if (session.state === 'signed_out') return null

  const { user, team } = session

  return (
    <Page>
      <PageHeader eyebrow="Application terrain" title="Réglages" />

      <PageBody className={s.body}>
        {writeFailed && (
          <InfoNote
            tone="warn"
            role="alert"
            className={s.failure}
            icon={<TriangleAlert size={15} />}
          >
            Ce réglage n’a pas pu être enregistré sur la tablette. Il reste tel qu’il était.
          </InfoNote>
        )}

        <Card className={s.card}>
          <CardHeader icon={<User size={18} aria-hidden />} title="Compte">
            <Tag className={s.lock}>
              <Lock size={14} aria-hidden />
              Non modifiable
            </Tag>
          </CardHeader>

          <CardBody>
            <div className={s.identity}>
              {/* Les initiales abrègent le nom imprimé juste à côté : la pastille est décorative
                  (`Avatar` est `aria-hidden`), et l'annoncer l'épellerait pour rien. */}
              <Avatar size="lg" initials={initialsOf(user)} />
              <div>
                <p className={s.name}>{nameOf(user)}</p>
                {/* Un agent **sans équipe** entre quand même (M6) : l'écran le dit au lieu de
                    laisser un vide, qui se lirait comme une donnée qui n'a pas chargé. */}
                <p className={s.team}>{team?.name ?? 'Sans équipe'}</p>
              </div>
            </div>

            <div className={s.fields}>
              <DataField label="Identifiant">
                <DataValue className={s.mono}>{user.login}</DataValue>
              </DataField>
              {/*
               * Le compte partagé de l'équipe (M22), qui départage deux équipes portant le même
               * nom — `Team.name` est dérivé des initiales des deux agents, deux équipes des
               * mêmes personnes sont donc homonymes. `null` quand l'équipe n'a pas de compte
               * rattaché, ce qui ne l'empêche pas de partir en tournée.
               */}
              <DataField label="Compte d’équipe">
                <DataValue className={s.mono}>{team?.accountLogin ?? 'Aucun'}</DataValue>
              </DataField>
            </div>

            <InfoNote className={s.note} icon={<Info size={15} />}>
              Les informations de l’agent sont gérées par le back-office et ne sont pas modifiables
              ici.
            </InfoNote>
          </CardBody>
        </Card>

        <Card className={s.card}>
          <CardHeader icon={<Wifi size={18} aria-hidden />} title="Synchronisation" />
          <CardBody className={s.rows}>
            <SwitchField
              icon={<Cloud size={20} aria-hidden />}
              label="Synchro automatique"
              hint="Envoyer les contrôles dès qu’une connexion est disponible"
              checked={settings.autoSync}
              onCheckedChange={(next) => void commute('autoSync', next)}
            />
            {/*
             * ⚠️ **Une des deux lignes de format du prototype, pas les deux.** Le schéma, la
             * tablette le produit : `createSchematicImagePort` (`@ghp/schematic`) le rastérise
             * en `image/jpeg`, la ligne dit donc vrai. Les photos, elle ne les produit pas —
             * la capture range le fichier de l'appareil tel quel (`capture`,
             * `inspections/routes/inspection-editor.tsx`), et son format est celui de la
             * tablette. Annoncer « JPEG » y promettrait ce qu'aucune ligne ne garantit.
             */}
            <SettingFact
              icon={<PenTool size={20} aria-hidden />}
              title="Format du schéma"
              hint="Schéma exporté avec le dossier client"
              value="JPEG"
            />
          </CardBody>
        </Card>

        <Card className={s.card}>
          <CardHeader icon={<SlidersHorizontal size={18} aria-hidden />} title="Contrôle terrain" />
          <CardBody className={s.rows}>
            {/*
             * ⚠️ Le prototype écrit « à l'ouverture d'un contrôle ». Notre app relève la position
             * **à la signature** (M20 : `lat`/`lon` sont le lieu du paraphe, pas celui du
             * branchement) : c'est le libellé qui suit le geste réel, sinon l'agent cherche à
             * l'ouverture une capture qui n'aura lieu qu'une heure plus tard.
             */}
            <SwitchField
              icon={<MapPin size={20} aria-hidden />}
              label="Géoréférencement automatique"
              hint="Relever la position GPS au moment de la signature"
              checked={settings.autoGeolocation}
              onCheckedChange={(next) => void commute('autoGeolocation', next)}
            />
          </CardBody>
        </Card>
      </PageBody>
    </Page>
  )
}

/**
 * Une rangée qui **constate** au lieu de régler — l'`info()` du prototype : même pastille, même
 * texte, un format à droite là où les autres portent un interrupteur.
 *
 * ⚠️ Ni `<label>` ni voile de cible, contrairement à `SwitchField` (`@ghp/ui`) : il n'y a aucun
 * contrôle à atteindre, et une rangée qui réagit au doigt sans rien faire se lit comme un
 * réglage en panne.
 */
function SettingFact({
  icon,
  title,
  hint,
  value,
}: {
  icon: ReactNode
  title: string
  hint: string
  value: string
}) {
  return (
    <div className={s.fact}>
      <span className={s.factIcon} aria-hidden>
        {icon}
      </span>
      <div className={s.factText}>
        <span className={s.factTitle}>{title}</span>
        <span className={s.factHint}>{hint}</span>
      </div>
      <Tag className={s.mono}>{value}</Tag>
    </div>
  )
}
